import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDownRight, ArrowUpRight, Mail, Globe } from "lucide-react";
import { MaskedLine, FadeUp, MaskedLineOnScroll } from "@/components/Reveal";

const LOGO =
  "https://customer-assets-0z36b82j.emergentagent.net/job_14e61164-3de2-41b4-9b37-cee521f8c798/artifacts/bu7idbnd_WhatsApp%20Image%202026-06-25%20at%2017.38.06%20%281%29.jpeg";
const EMAIL = "info@ayyapaneni.in";
const DOMAIN = "ayyapaneni.in";

const HERO_BG =
  "https://images.pexels.com/photos/586568/pexels-photo-586568.jpeg";

const CHAPTERS = [
  {
    num: "01",
    title: "The Vision",
    body: "Every structure begins as an idea drawn against the sky. We shape ambition into permanence — architecture that carries intent in every line.",
    img: "https://images.unsplash.com/photo-1569258592171-357ea26da4df",
    tag: "Design & Intent",
  },
  {
    num: "02",
    title: "The Process",
    body: "Precision engineering meets relentless craft. From foundation to finish, we build with the discipline of makers who refuse to compromise.",
    img: "https://images.unsplash.com/photo-1768426221603-124a1250955f",
    tag: "Engineering & Craft",
  },
  {
    num: "03",
    title: "The Result",
    body: "Spaces that outlast trends. Buildings designed to be lived in, remembered, and passed on — the quiet legacy of work done well.",
    img: "https://images.pexels.com/photos/4458193/pexels-photo-4458193.jpeg",
    tag: "Legacy & Detail",
  },
];

/* ---------------- Header ---------------- */
const Header = () => (
  <motion.header
    data-testid="site-header"
    initial={{ y: -80, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
    className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-navy/50 border-b border-white/10"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
      <img
        src={LOGO}
        alt="Ayyapaneni Constructions"
        data-testid="header-logo"
        className="h-9 md:h-10 w-auto object-contain"
      />
      <a
        href="#contact"
        data-testid="header-contact-link"
        className="group hidden sm:flex items-center gap-2 text-[0.7rem] md:text-xs tracking-[0.2em] uppercase font-semibold text-paper/80 hover:text-gold transition-colors duration-300"
      >
        Contact
        <ArrowUpRight
          size={14}
          className="text-gold transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </a>
    </div>
  </motion.header>
);

/* ---------------- Hero ---------------- */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.28]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-center"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0 will-change-transform"
      >
        <img
          src={HERO_BG}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-navy/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-navy/70" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 pt-24"
      >
        <MaskedLine delay={0.5}>
          <span className="inline-flex items-center gap-3 text-gold text-xs md:text-sm tracking-[0.35em] uppercase font-semibold">
            <span className="h-px w-10 bg-gold/60" />
            Coming Soon
          </span>
        </MaskedLine>

        <h1 className="font-serif-brand text-paper mt-6 leading-[0.9] tracking-tight">
          <MaskedLine delay={0.65} className="text-[15vw] md:text-[11vw] lg:text-[9.5rem] font-medium">
            Ayyapaneni
          </MaskedLine>
          <MaskedLine
            delay={0.82}
            className="text-[9vw] md:text-[6vw] lg:text-[5rem] font-light italic text-brass"
          >
            Constructions
          </MaskedLine>
        </h1>

        <div className="mt-10 grid md:grid-cols-12 gap-8 items-end">
          <FadeUp delay={1.05} className="md:col-span-6">
            <p className="text-paper/70 text-base md:text-lg leading-relaxed max-w-xl font-light">
              We are building something monumental. A new landmark in design,
              engineering and craft is on its way — where every structure is
              built to endure.
            </p>
          </FadeUp>
          <FadeUp delay={1.2} className="md:col-span-6 md:text-right">
            <a
              href={`mailto:${EMAIL}`}
              data-testid="hero-email-link"
              className="group inline-flex items-center gap-3 text-paper hover:text-gold transition-colors duration-300"
            >
              <span className="text-xs tracking-[0.25em] uppercase font-semibold text-gold">
                Get in touch
              </span>
              <span className="font-serif-brand text-2xl md:text-3xl border-b border-transparent group-hover:border-gold transition-colors duration-300">
                {EMAIL}
              </span>
            </a>
          </FadeUp>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="text-[0.65rem] tracking-[0.3em] uppercase text-paper/50">
          Scroll
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDownRight size={18} className="text-gold" />
        </motion.div>
      </motion.div>
    </section>
  );
};

/* ---------------- Marquee ---------------- */
const Ribbon = () => (
  <div
    data-testid="marquee-ribbon"
    className="border-y border-gold/25 bg-navy-light py-6 md:py-8 overflow-hidden"
  >
    <Marquee speed={40} gradient={false} autoFill>
      {Array.from({ length: 4 }).map((_, i) => (
        <span
          key={i}
          className="font-serif-brand text-3xl md:text-5xl text-stone/90 mx-8 flex items-center gap-8"
        >
          Ayyapaneni Constructions
          <span className="text-gold text-2xl">✦</span>
          <span className="italic text-brass">Est. 2026</span>
          <span className="text-gold text-2xl">✦</span>
          Shaping Tomorrow
          <span className="text-gold text-2xl">✦</span>
        </span>
      ))}
    </Marquee>
  </div>
);

/* ---------------- Chapter (parallax image) ---------------- */
const Chapter = ({ chapter, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const flip = index % 2 === 1;

  return (
    <div
      ref={ref}
      data-testid={`chapter-${chapter.num}`}
      className="grid md:grid-cols-12 gap-8 md:gap-12 items-center py-16 md:py-24"
    >
      {/* Image */}
      <div
        className={`md:col-span-7 ${flip ? "md:order-2" : "md:order-1"}`}
      >
        <FadeUp>
          <div className="relative overflow-hidden aspect-[4/3] border border-gold/20">
            <motion.img
              style={{ y: imgY, scale: 1.2 }}
              src={chapter.img}
              alt={chapter.title}
              className="w-full h-full object-cover will-change-transform"
            />
            <div className="absolute inset-0 bg-navy/20" />
            <span className="absolute top-4 left-4 text-[0.65rem] tracking-[0.25em] uppercase text-paper/90 bg-navy/60 backdrop-blur px-3 py-1.5 border border-white/10">
              {chapter.tag}
            </span>
          </div>
        </FadeUp>
      </div>

      {/* Text */}
      <div
        className={`md:col-span-5 ${flip ? "md:order-1" : "md:order-2"}`}
      >
        <FadeUp>
          <span className="font-serif-brand text-gold text-6xl md:text-8xl leading-[0.85] block pb-1">
            {chapter.num}
          </span>
        </FadeUp>
        <h3 className="font-serif-brand text-4xl md:text-5xl text-paper mt-4 mb-5">
          <MaskedLineOnScroll delay={0.05}>{chapter.title}</MaskedLineOnScroll>
        </h3>
        <FadeUp delay={0.15}>
          <div className="h-px w-16 bg-gold/50 mb-5" />
          <p className="text-paper/65 text-base md:text-lg leading-relaxed font-light">
            {chapter.body}
          </p>
        </FadeUp>
      </div>
    </div>
  );
};

/* ---------------- Manifesto ---------------- */
const Manifesto = () => (
  <section
    data-testid="manifesto-section"
    className="relative bg-navy py-24 md:py-32"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="grid md:grid-cols-12 gap-8 mb-8">
        <div className="md:col-span-4">
          <FadeUp>
            <span className="text-gold text-xs tracking-[0.3em] uppercase font-semibold">
              Our Philosophy
            </span>
          </FadeUp>
        </div>
        <div className="md:col-span-8">
          <h2 className="font-serif-brand text-4xl md:text-6xl text-paper leading-[1.05]">
            <MaskedLineOnScroll>We don&apos;t just build.</MaskedLineOnScroll>
            <MaskedLineOnScroll delay={0.08} className="italic text-brass">
              We shape what lasts.
            </MaskedLineOnScroll>
          </h2>
        </div>
      </div>

      <div className="divide-y divide-white/5">
        {CHAPTERS.map((c, i) => (
          <Chapter key={c.num} chapter={c} index={i} />
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Contact / Footer ---------------- */
const ContactFooter = () => (
  <footer
    id="contact"
    data-testid="contact-footer"
    className="relative bg-stone text-navy py-24 md:py-32 overflow-hidden"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <FadeUp>
        <span className="text-terracotta text-xs tracking-[0.3em] uppercase font-semibold">
          Let&apos;s Talk
        </span>
      </FadeUp>

      <a
        href={`mailto:${EMAIL}`}
        data-testid="footer-email-link"
        className="group block mt-6 mb-16"
      >
        <h2 className="font-serif-brand text-navy leading-[0.95]">
          <MaskedLineOnScroll className="text-[13vw] md:text-[8vw] lg:text-[7rem] font-medium">
            Build with us.
          </MaskedLineOnScroll>
        </h2>
        <span className="mt-6 inline-flex items-center gap-3 font-sans-brand text-xl md:text-3xl text-navy/80 group-hover:text-terracotta transition-colors duration-300">
          <Mail className="text-terracotta" size={26} />
          <span className="border-b-2 border-navy/20 group-hover:border-terracotta transition-colors duration-300">
            {EMAIL}
          </span>
          <ArrowUpRight
            size={26}
            className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
          />
        </span>
      </a>

      <div className="h-px w-full bg-navy/15 mb-10" />

      <div className="grid md:grid-cols-12 gap-8 items-end">
        <div className="md:col-span-6">
          <img
            src={LOGO}
            alt="Ayyapaneni Constructions"
            data-testid="footer-logo"
            className="h-12 w-auto object-contain mb-5"
          />
          <p className="text-navy/60 text-sm max-w-sm leading-relaxed">
            A new standard in construction, engineering and design. Our full
            experience launches soon.
          </p>
        </div>
        <div className="md:col-span-6 md:text-right space-y-3">
          <a
            href={`https://${DOMAIN}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-domain-link"
            className="inline-flex items-center gap-2 text-navy hover:text-terracotta transition-colors duration-300 font-medium"
          >
            <Globe size={16} className="text-terracotta" />
            {DOMAIN}
          </a>
          <p className="text-navy/50 text-xs tracking-wide">
            © {new Date().getFullYear()} Ayyapaneni Constructions. All rights
            reserved.
          </p>
        </div>
      </div>
    </div>
  </footer>
);

/* ---------------- Page ---------------- */
export default function ComingSoon() {
  return (
    <div data-testid="coming-soon-page" className="bg-navy">
      <Header />
      <Hero />
      <Ribbon />
      <Manifesto />
      <ContactFooter />
    </div>
  );
}
